import { Card, ConfirmModal, Modal, Panel } from '@/components/Bicester';
import Image from '@/components/Bicester/Image';
import { Form } from '@/components/formik-rsuite';
import PageLoader from '@/components/shared/PageLoader';
import { getCityCode, getConcatCity } from '@/components/utils';
import { PAGE_TYPE, ROLE_TYPE } from '@/constants';
import useLoading from '@/hooks/useLoading';
import usePageType from '@/hooks/usePageType';
import useRouter from '@/hooks/useRouter';
import { useReadyInfo } from '@/providers/ReadyProvider';
import { fetchAddeEditNotMember, fetchNotMemberInfo, fetchPhoneVerify } from '@/services/account';
import { getParamsWithNilToString } from '@/services/utils';
import { replace } from '@/utils/history';
import { useModal } from '@ebay/nice-modal-react';
import { useRequest } from 'ahooks';
import { FormikProvider, useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import { Alert, IconButton } from 'rsuite';
import * as Yup from 'yup';
import ServiceRecord from './components/ServiceRecord';

interface NotMemberInfoForm {
  name: string; // 客户名
  sex: string; // 性别
  phone: string; // 手机号
  email: string; // 电子邮箱
  address: string; // 地址
  city: string; // 城市/地区
  remark: string; // 备注
}

const NotMember = () => {
  const pageType = usePageType();
  const modal = useModal(ConfirmModal);
  const { profile, allCities } = useReadyInfo();
  const { shopping_village } = profile;

  const { match, query } = useRouter<{ id: string }>();

  const isCreate = pageType === PAGE_TYPE.CREATE;
  const [isEdit, setIsEdit] = useState(false);

  const defaultNotMemberInfo: NotMemberInfoForm = {
    name: '',
    sex: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    remark: '',
  };

  const [defaultInfo, setDefaultInfo] = useState<NotMemberInfoForm>(defaultNotMemberInfo);

  const [saveInfoModal, setSaveInfoModal] = useState({
    show: false,
    content: '',
    confirm: '',
    cancel: '',
    onConfirm: null,
  });

  const {
    run: getNotMemberInfo,
    data: notMemberInfoData,
    loading: notMemberInfoLoading,
  } = useRequest(fetchNotMemberInfo, {
    manual: true,
  });

  const { run: addeOrEditNotMember, loading: addeOrEditNotMemberLoading } = useRequest(
    fetchAddeEditNotMember,
    {
      manual: true,
    }
  );

  const formik = useFormik({
    initialValues: defaultInfo,
    enableReinitialize: true, // initialValues默认值受控
    validateOnChange: false,
    validationSchema: Yup.object({
      name: Yup.string().required('请输入客户名'),
      phone: Yup.string().test((value, context) => {
        return fetchPhoneVerify({
          type: 2,
          phone: value,
          ...(!isCreate && { id: match.params.id }),
        }).then((res) => {
          if (res?.is_verify) {
            return true;
          }
          return context.createError({ message: res?.verify_msg });
        });
      }),
      email: Yup.string().email('请输入正确的邮箱'),
    }),
    onSubmit(values, actions) {
      if (formik.isValid) {
        setSaveInfoModal({
          show: true,
          content: '是否保存本次编辑？',
          confirm: '保存',
          cancel: '暂不',
          onConfirm: () => handleSave(values),
        });
      }
    },
  });

  const fetchInfo = (noMemberId?: string | number) => {
    getNotMemberInfo(noMemberId || match.params.id).then((res) => {
      const { name, sex, phone, email, address, city, remark } = res;
      const defaultValues = {
        name,
        sex,
        phone,
        email,
        address,
        city: getCityCode(city, allCities),
        remark,
      };
      formik.setValues(defaultValues);
      setDefaultInfo(defaultValues);
    });
  };

  const handleSave = (values) => {
    const newValues = Object.assign({}, values, {
      city: getConcatCity(values.city, allCities),
    });
    const params = getParamsWithNilToString(newValues);
    addeOrEditNotMember({
      data: {
        ...params,
        ...(isEdit && { id: match.params.id }),
      },
    }).then((res) => {
      if (res?.id) {
        const { id } = res;
        setIsEdit(false);
        Alert.success('保存成功');
        // 创建成功后跳到详情页，修改成功停留在本页且重新获取用户信息
        if (isCreate) {
          formik.resetForm(); // 重置后不会被guard拦截
          replace(`/customer-account/no-member/detail/${id}`);
        }
        fetchInfo(id);
      }
    });
    handleHideModal();
  };

  useEffect(() => {
    if (pageType === PAGE_TYPE.CREATE) {
      // 重置表单
      formik.resetForm();
    }
  }, [pageType]);

  useEffect(() => {
    if (pageType === PAGE_TYPE.DETAIL) {
      fetchInfo();
    } else if (pageType === PAGE_TYPE.CREATE) {
      if (query?.phone) {
        formik.setFieldValue('phone', query?.phone);
      }
    }
  }, []);

  const handleHideModal = () => {
    setSaveInfoModal({
      ...saveInfoModal,
      show: false,
    });
  };

  const loading = useLoading(notMemberInfoLoading || addeOrEditNotMemberLoading);

  return (
    <>
      {loading && <PageLoader />}
      <FormikProvider value={formik}>
        <Form guard>
          <Panel
            title="客户信息"
            sideBarBtn={
              // 创建页没有快捷服务导航，GE经理、GE领班、GE迎宾大使、DRT导购大使(会员可用的服务：上传小票和优惠停车，非会员没有))才有快捷服务导航
              !isCreate &&
              [ROLE_TYPE.MANAGER, ROLE_TYPE.GANGER, ROLE_TYPE.AMBASSADOR].includes(profile?.role)
            }
            toolbar={
              <div className="flex justify-between w-fit">
                <IconButton
                  className="flex items-center mr-[30px]"
                  disabled={isCreate}
                  onClick={() => {
                    if (isEdit) {
                      if (formik.dirty) {
                        modal.show({
                          contentText: '信息还没保存，确定离开吗？',
                          submitText: '确定',
                          cancelText: '取消',
                          onSubmit: () => {
                            formik.resetForm();
                            setIsEdit(!isEdit);
                          },
                        });
                      } else {
                        formik.resetForm();
                        setIsEdit(!isEdit);
                      }
                    } else {
                      setIsEdit(!isEdit);
                    }
                  }}
                >
                  <Image src="/resources/images/edit.svg" />
                  <div className="text-[12px] text-[#999999] ml-[5px]">
                    {isEdit ? '取消' : '编辑'}
                  </div>
                </IconButton>
                <IconButton
                  type="submit"
                  className="flex items-center"
                  disabled={!isCreate && !isEdit}
                  onClick={() => {
                    formik.submitForm();
                  }}
                >
                  <Image src="/resources/images/save.svg" />
                  <div className="text-[12px] text-[#999999] ml-[5px]">保存</div>
                </IconButton>
              </div>
            }
          >
            <Card title="个人信息" className="w-[400px]">
              <Card.Input isEdit={isCreate || isEdit} required label="客户名" formKey="name" />
              <Card.Picker
                isEdit={isCreate || isEdit}
                label="性别"
                formKey="sex"
                pickerType="sex"
              />
              <Card.Input isEdit={isCreate || isEdit} label="手机号" formKey="phone" />
              <Card.Input isEdit={isCreate || isEdit} label="电子邮箱" formKey="email" />
              <Card.Input isEdit={isCreate || isEdit} label="地址" formKey="address" />
              <Card.CityPicker
                isEdit={isCreate || isEdit}
                label="城市/地区"
                formKey="city"
                value={notMemberInfoData?.city}
              />
              <Card.Input
                notEditable
                label="注册购物村"
                formKey="village"
                inputType="village"
                value={isCreate ? shopping_village : notMemberInfoData?.shopping_village}
              />
              <Card.Input
                isEdit={isCreate || isEdit}
                label="备注"
                formKey="remark"
                inputType="textarea"
              />
            </Card>
          </Panel>
        </Form>
      </FormikProvider>
      {!isCreate && <ServiceRecord />}
      <Modal
        show={saveInfoModal.show}
        content={saveInfoModal.content}
        confirm={saveInfoModal.confirm}
        cancel={saveInfoModal.cancel}
        onConfirm={saveInfoModal.onConfirm}
        onCancel={handleHideModal}
      />
    </>
  );
};

export default NotMember;
